/**
 * Who answers for an item and who does it (decision 0035, note 7): derived, never stored. The OWNER answers (a person
 * or a persistent agent); the ASSIGNEE does it (a person, a new worker for the owner agent, or the owner itself).
 */
import { agentOf, areaOf, leadOf } from "./state"
import { ancestorsOf, isPlannable } from "./tree"
import type { Agent, AgentId, Item, ProjectState, UserId } from "./types"

const liveAgent = (s: ProjectState, id: AgentId): Agent | undefined => {
  const a = agentOf(s, id)
  return a && a.status !== "retired" ? a : undefined
}

/**
 * The agent above an item (or above a new item with this parent and area): the nearest item above in the same area
 * owned by an agent; else the area's agent (`Area.intakeAgentId`); else the lead. Default owner of new items, the
 * `overdue` wake and a cancelled predecessor's `blocker` wake.
 */
export const agentAboveOf = (
  s: ProjectState,
  where: { readonly parentId: Item["parentId"]; readonly areaId: Item["areaId"]; readonly id?: Item["id"] },
): Agent | undefined => {
  const chain =
    where.parentId === null ? [] : ancestorsOf(s, { id: where.id ?? ("" as never), parentId: where.parentId })
  for (const a of chain) {
    if (a.areaId !== where.areaId || a.owner?._tag !== "agent") continue
    const agent = liveAgent(s, a.owner.agentId)
    if (agent) return agent
  }
  const area = where.areaId ? areaOf(s, where.areaId) : undefined
  const areaAgent = area?.intakeAgentId ? liveAgent(s, area.intakeAgentId) : undefined
  return areaAgent ?? leadOf(s)
}

export const agentAbove = (s: ProjectState, item: Item): Agent | undefined => agentAboveOf(s, item)

/**
 * The person accountable for an item: its owner if a person; else the nearest person owner above; else the owner
 * agent's human owner (or the nearest agent owner's above); else the project's owner. Approvals, escalations, human
 * checks and an agent's ask_human route here.
 */
export const accountablePerson = (s: ProjectState, item: Item): UserId | null => {
  if (item.owner?._tag === "user") return item.owner.userId
  const up = ancestorsOf(s, item)
  for (const a of up) if (a.owner?._tag === "user") return a.owner.userId
  for (const t of [item, ...up])
    if (t.owner?._tag === "agent") {
      const agent = agentOf(s, t.owner.agentId)
      if (agent) return agent.ownerId
    }
  return s.project?.ownerId ?? null
}

/** The owner agent of an item (null for a person owner or none). */
export const ownerAgentOf = (s: ProjectState, item: Pick<Item, "owner">): Agent | undefined =>
  item.owner?._tag === "agent" ? agentOf(s, item.owner.agentId) : undefined

/** The Assignee line of an item: who does it, in words the UI renders. */
export type AssigneeLine =
  | { readonly _tag: "person"; readonly userId: UserId }
  | { readonly _tag: "worker"; readonly agentId: AgentId | null; readonly preset: string | null }
  | { readonly _tag: "agent_itself"; readonly agentId: AgentId }
  /** Plannable items (their children do the work) and items in triage. */
  | { readonly _tag: "none" }
  /** A leaf that should have an assignee and has none. */
  | { readonly _tag: "nobody" }

export const assigneeLine = (s: ProjectState, item: Item): AssigneeLine => {
  const a = item.assignee
  if (a?._tag === "user") return { _tag: "person", userId: a.userId }
  if (a?._tag === "worker")
    return { _tag: "worker", agentId: ownerAgentOf(s, item)?.id ?? null, preset: a.preset }
  if (a?._tag === "agent") return { _tag: "agent_itself", agentId: a.agentId }
  return isPlannable(item) || item.status === "triage" ? { _tag: "none" } : { _tag: "nobody" }
}

/** The person behind a principal, for routing: a user, or an agent's human owner. */
export const personOf = (
  s: ProjectState,
  p:
    | { readonly _tag: "user"; readonly userId: UserId }
    | { readonly _tag: "agent"; readonly agentId: AgentId },
): UserId | null => (p._tag === "user" ? p.userId : (agentOf(s, p.agentId)?.ownerId ?? null))
